/**
 * Options WebSocket Market Streams
 *
 * Access market data, manage accounts, and trade Binance Options.
 *
 * The version of the OpenAPI document: 1.0.0
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import {
    WebsocketStreamsBase,
    WebsocketStream,
    assertParamExists,
    replaceWebsocketStreamsPlaceholders,
    createStreamHandler,
} from '@binance/common';
import type {
    DiffBookDepthStreamsResponse,
    Hour24TickerResponse,
    IndividualSymbolBookTickerStreamsResponse,
    PartialBookDepthStreamsResponse,
    TradeStreamsResponse,
} from '../types';

const PublicApiParamCreator = function () {
    return {
        /**
         * Bids and asks, pushed every 500 milliseconds, 100 milliseconds (if existing)
         *
         * Update Speed: 100ms or 500ms
         *
         * @summary Diff Book Depth Streams
         * @param {string} symbol The symbol parameter
         * @param {DiffBookDepthStreamsUpdateSpeedEnum} updateSpeed WebSocket stream update speed
         * @param {number} [id] Unique WebSocket request ID.
         *
         * @throws {RequiredError}
         */
        diffBookDepthStreams: (
            symbol: string,
            updateSpeed: DiffBookDepthStreamsUpdateSpeedEnum,
            id?: number
        ): string => {
            // verify required parameter 'symbol' is not null or undefined
            assertParamExists('diffBookDepthStreams', 'symbol', symbol);
            // verify required parameter 'updateSpeed' is not null or undefined
            assertParamExists('diffBookDepthStreams', 'updateSpeed', updateSpeed);

            return replaceWebsocketStreamsPlaceholders('/<symbol>@depth@<updateSpeed>'.slice(1), {
                symbol,
                updateSpeed,
                id,
            });
        },
        /**
         * 24hr ticker info for all symbols. Only symbols whose ticker info changed will be sent.
         *
         * Update Speed: 1000ms
         *
         * @summary 24-hour TICKER
         * @param {string} symbol The symbol parameter
         * @param {number} [id] Unique WebSocket request ID.
         * @param {string} [expirationDate] The expiration date parameter
         *
         * @throws {RequiredError}
         */
        hour24Ticker: (symbol: string, id?: number, expirationDate?: string): string => {
            // verify required parameter 'symbol' is not null or undefined
            assertParamExists('hour24Ticker', 'symbol', symbol);

            return replaceWebsocketStreamsPlaceholders(
                '/<symbol>@optionTicker<expirationDate>'.slice(1),
                { symbol, id, expirationDate }
            );
        },
        /**
         * Pushes any update to the best bid or ask's price or quantity in real-time for a specified symbol.
         *
         * Update Speed: Real-Time
         *
         * @summary Individual Symbol Book Ticker Streams
         * @param {string} symbol The symbol parameter
         * @param {number} [id] Unique WebSocket request ID.
         *
         * @throws {RequiredError}
         */
        individualSymbolBookTickerStreams: (symbol: string, id?: number): string => {
            // verify required parameter 'symbol' is not null or undefined
            assertParamExists('individualSymbolBookTickerStreams', 'symbol', symbol);

            return replaceWebsocketStreamsPlaceholders('/<symbol>@bookTicker'.slice(1), {
                symbol,
                id,
            });
        },
        /**
         * Top <levels> bids and asks. Valid <levels> are 5, 10, 20.
         *
         * Update Speed: 100ms or 500ms
         *
         * @summary Partial Book Depth Streams
         * @param {string} symbol The symbol parameter
         * @param {PartialBookDepthStreamsLevelEnum} level The level parameter
         * @param {PartialBookDepthStreamsUpdateSpeedEnum} updateSpeed WebSocket stream update speed
         * @param {number} [id] Unique WebSocket request ID.
         *
         * @throws {RequiredError}
         */
        partialBookDepthStreams: (
            symbol: string,
            level: PartialBookDepthStreamsLevelEnum,
            updateSpeed: PartialBookDepthStreamsUpdateSpeedEnum,
            id?: number
        ): string => {
            // verify required parameter 'symbol' is not null or undefined
            assertParamExists('partialBookDepthStreams', 'symbol', symbol);
            // verify required parameter 'level' is not null or undefined
            assertParamExists('partialBookDepthStreams', 'level', level);
            // verify required parameter 'updateSpeed' is not null or undefined
            assertParamExists('partialBookDepthStreams', 'updateSpeed', updateSpeed);

            return replaceWebsocketStreamsPlaceholders(
                '/<symbol>@depth<level>@<updateSpeed>'.slice(1),
                { symbol, level, updateSpeed, id }
            );
        },
        /**
         * The Trade Streams push raw trade information for specific symbol or underlying asset. E.g.[btcusdt@optionTrade](wss://fstream.binance.com/public/stream?streams=btcusdt@optionTrade)
         *
         * Update Speed: 50ms
         *
         * @summary Trade Streams
         * @param {string} symbol The symbol parameter
         * @param {number} [id] Unique WebSocket request ID.
         *
         * @throws {RequiredError}
         */
        tradeStreams: (symbol: string, id?: number): string => {
            // verify required parameter 'symbol' is not null or undefined
            assertParamExists('tradeStreams', 'symbol', symbol);

            return replaceWebsocketStreamsPlaceholders('/<symbol>@optionTrade'.slice(1), {
                symbol,
                id,
            });
        },
    };
};

/**
 * PublicApi - interface
 * @interface PublicApi
 */
export interface PublicApiInterface {
    /**
     * Bids and asks, pushed every 500 milliseconds, 100 milliseconds (if existing)
     *
     * Update Speed: 100ms or 500ms
     *
     * @summary Diff Book Depth Streams
     * @param {DiffBookDepthStreamsRequest} requestParameters Request parameters.
     *
     * @returns {WebsocketStream<DiffBookDepthStreamsResponse>}
     * @throws {RequiredError}
     * @memberof PublicApiInterface
     */
    diffBookDepthStreams(
        requestParameters: DiffBookDepthStreamsRequest
    ): WebsocketStream<DiffBookDepthStreamsResponse>;

    /**
     * 24hr ticker info for all symbols. Only symbols whose ticker info changed will be sent.
     *
     * Update Speed: 1000ms
     *
     * @summary 24-hour TICKER
     * @param {Hour24TickerRequest} requestParameters Request parameters.
     *
     * @returns {WebsocketStream<Hour24TickerResponse>}
     * @throws {RequiredError}
     * @memberof PublicApiInterface
     */
    hour24Ticker(requestParameters: Hour24TickerRequest): WebsocketStream<Hour24TickerResponse>;

    /**
     * Pushes any update to the best bid or ask's price or quantity in real-time for a specified symbol.
     *
     * Update Speed: Real-Time
     *
     * @summary Individual Symbol Book Ticker Streams
     * @param {IndividualSymbolBookTickerStreamsRequest} requestParameters Request parameters.
     *
     * @returns {WebsocketStream<IndividualSymbolBookTickerStreamsResponse>}
     * @throws {RequiredError}
     * @memberof PublicApiInterface
     */
    individualSymbolBookTickerStreams(
        requestParameters: IndividualSymbolBookTickerStreamsRequest
    ): WebsocketStream<IndividualSymbolBookTickerStreamsResponse>;

    /**
     * Top <levels> bids and asks. Valid <levels> are 5, 10, 20.
     *
     * Update Speed: 100ms or 500ms
     *
     * @summary Partial Book Depth Streams
     * @param {PartialBookDepthStreamsRequest} requestParameters Request parameters.
     *
     * @returns {WebsocketStream<PartialBookDepthStreamsResponse>}
     * @throws {RequiredError}
     * @memberof PublicApiInterface
     */
    partialBookDepthStreams(
        requestParameters: PartialBookDepthStreamsRequest
    ): WebsocketStream<PartialBookDepthStreamsResponse>;

    /**
     * The Trade Streams push raw trade information for specific symbol or underlying asset. E.g.[btcusdt@optionTrade](wss://fstream.binance.com/public/stream?streams=btcusdt@optionTrade)
     *
     * Update Speed: 50ms
     *
     * @summary Trade Streams
     * @param {TradeStreamsRequest} requestParameters Request parameters.
     *
     * @returns {WebsocketStream<TradeStreamsResponse>}
     * @throws {RequiredError}
     * @memberof PublicApiInterface
     */
    tradeStreams(requestParameters: TradeStreamsRequest): WebsocketStream<TradeStreamsResponse>;
}

/**
 * Request parameters for diffBookDepthStreams operation in PublicApi.
 * @interface DiffBookDepthStreamsRequest
 */
export interface DiffBookDepthStreamsRequest {
    /**
     * The symbol parameter
     * @type {string}
     * @memberof PublicApiDiffBookDepthStreams
     */
    readonly symbol: string;

    /**
     * WebSocket stream update speed
     * @type {'100ms' | '500ms'}
     * @memberof PublicApiDiffBookDepthStreams
     */
    readonly updateSpeed: DiffBookDepthStreamsUpdateSpeedEnum;

    /**
     * Unique WebSocket request ID.
     * @type {number}
     * @memberof PublicApiDiffBookDepthStreams
     */
    readonly id?: number;
}

/**
 * Request parameters for hour24Ticker operation in PublicApi.
 * @interface Hour24TickerRequest
 */
export interface Hour24TickerRequest {
    /**
     * The symbol parameter
     * @type {string}
     * @memberof PublicApiHour24Ticker
     */
    readonly symbol: string;

    /**
     * Unique WebSocket request ID.
     * @type {number}
     * @memberof PublicApiHour24Ticker
     */
    readonly id?: number;

    /**
     * The expiration date parameter
     * @type {string}
     * @memberof PublicApiHour24Ticker
     */
    readonly expirationDate?: string;
}

/**
 * Request parameters for individualSymbolBookTickerStreams operation in PublicApi.
 * @interface IndividualSymbolBookTickerStreamsRequest
 */
export interface IndividualSymbolBookTickerStreamsRequest {
    /**
     * The symbol parameter
     * @type {string}
     * @memberof PublicApiIndividualSymbolBookTickerStreams
     */
    readonly symbol: string;

    /**
     * Unique WebSocket request ID.
     * @type {number}
     * @memberof PublicApiIndividualSymbolBookTickerStreams
     */
    readonly id?: number;
}

/**
 * Request parameters for partialBookDepthStreams operation in PublicApi.
 * @interface PartialBookDepthStreamsRequest
 */
export interface PartialBookDepthStreamsRequest {
    /**
     * The symbol parameter
     * @type {string}
     * @memberof PublicApiPartialBookDepthStreams
     */
    readonly symbol: string;

    /**
     * The level parameter
     * @type {'5' | '10' | '20'}
     * @memberof PublicApiPartialBookDepthStreams
     */
    readonly level: PartialBookDepthStreamsLevelEnum;

    /**
     * WebSocket stream update speed
     * @type {'100ms' | '500ms'}
     * @memberof PublicApiPartialBookDepthStreams
     */
    readonly updateSpeed: PartialBookDepthStreamsUpdateSpeedEnum;

    /**
     * Unique WebSocket request ID.
     * @type {number}
     * @memberof PublicApiPartialBookDepthStreams
     */
    readonly id?: number;
}

/**
 * Request parameters for tradeStreams operation in PublicApi.
 * @interface TradeStreamsRequest
 */
export interface TradeStreamsRequest {
    /**
     * The symbol parameter
     * @type {string}
     * @memberof PublicApiTradeStreams
     */
    readonly symbol: string;

    /**
     * Unique WebSocket request ID.
     * @type {number}
     * @memberof PublicApiTradeStreams
     */
    readonly id?: number;
}

/**
 * PublicApi - interface
 * @class PublicApi
 * @extends {WebsocketStreamsBase}
 */
export class PublicApi implements PublicApiInterface {
    private readonly websocketBase: WebsocketStreamsBase;
    private localVarParamCreator;

    constructor(websocketBase: WebsocketStreamsBase) {
        this.websocketBase = websocketBase;
        this.localVarParamCreator = PublicApiParamCreator();
    }

    /**
     * Bids and asks, pushed every 500 milliseconds, 100 milliseconds (if existing)
     *
     * Update Speed: 100ms or 500ms
     *
     * @summary Diff Book Depth Streams
     * @param {DiffBookDepthStreamsRequest} requestParameters Request parameters.
     * @returns {WebsocketStream<DiffBookDepthStreamsResponse>}
     * @throws {RequiredError}
     * @memberof PublicApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-options/api/ws-streams/public#diff-book-depth-streams Binance API Documentation}
     */
    public diffBookDepthStreams(
        requestParameters: DiffBookDepthStreamsRequest
    ): WebsocketStream<DiffBookDepthStreamsResponse> {
        const stream = this.localVarParamCreator.diffBookDepthStreams(
            requestParameters?.symbol,
            requestParameters?.updateSpeed,
            requestParameters?.id
        );

        return createStreamHandler<DiffBookDepthStreamsResponse>(
            this.websocketBase,
            stream,
            requestParameters?.id,
            'public'
        );
    }

    /**
     * 24hr ticker info for all symbols. Only symbols whose ticker info changed will be sent.
     *
     * Update Speed: 1000ms
     *
     * @summary 24-hour TICKER
     * @param {Hour24TickerRequest} requestParameters Request parameters.
     * @returns {WebsocketStream<Hour24TickerResponse>}
     * @throws {RequiredError}
     * @memberof PublicApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-options/api/ws-streams/public#hour24-ticker Binance API Documentation}
     */
    public hour24Ticker(
        requestParameters: Hour24TickerRequest
    ): WebsocketStream<Hour24TickerResponse> {
        const stream = this.localVarParamCreator.hour24Ticker(
            requestParameters?.symbol,
            requestParameters?.id,
            requestParameters?.expirationDate
        );

        return createStreamHandler<Hour24TickerResponse>(
            this.websocketBase,
            stream,
            requestParameters?.id,
            'public'
        );
    }

    /**
     * Pushes any update to the best bid or ask's price or quantity in real-time for a specified symbol.
     *
     * Update Speed: Real-Time
     *
     * @summary Individual Symbol Book Ticker Streams
     * @param {IndividualSymbolBookTickerStreamsRequest} requestParameters Request parameters.
     * @returns {WebsocketStream<IndividualSymbolBookTickerStreamsResponse>}
     * @throws {RequiredError}
     * @memberof PublicApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-options/api/ws-streams/public#individual-symbol-book-ticker-streams Binance API Documentation}
     */
    public individualSymbolBookTickerStreams(
        requestParameters: IndividualSymbolBookTickerStreamsRequest
    ): WebsocketStream<IndividualSymbolBookTickerStreamsResponse> {
        const stream = this.localVarParamCreator.individualSymbolBookTickerStreams(
            requestParameters?.symbol,
            requestParameters?.id
        );

        return createStreamHandler<IndividualSymbolBookTickerStreamsResponse>(
            this.websocketBase,
            stream,
            requestParameters?.id,
            'public'
        );
    }

    /**
     * Top <levels> bids and asks. Valid <levels> are 5, 10, 20.
     *
     * Update Speed: 100ms or 500ms
     *
     * @summary Partial Book Depth Streams
     * @param {PartialBookDepthStreamsRequest} requestParameters Request parameters.
     * @returns {WebsocketStream<PartialBookDepthStreamsResponse>}
     * @throws {RequiredError}
     * @memberof PublicApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-options/api/ws-streams/public#partial-book-depth-streams Binance API Documentation}
     */
    public partialBookDepthStreams(
        requestParameters: PartialBookDepthStreamsRequest
    ): WebsocketStream<PartialBookDepthStreamsResponse> {
        const stream = this.localVarParamCreator.partialBookDepthStreams(
            requestParameters?.symbol,
            requestParameters?.level,
            requestParameters?.updateSpeed,
            requestParameters?.id
        );

        return createStreamHandler<PartialBookDepthStreamsResponse>(
            this.websocketBase,
            stream,
            requestParameters?.id,
            'public'
        );
    }

    /**
     * The Trade Streams push raw trade information for specific symbol or underlying asset. E.g.[btcusdt@optionTrade](wss://fstream.binance.com/public/stream?streams=btcusdt@optionTrade)
     *
     * Update Speed: 50ms
     *
     * @summary Trade Streams
     * @param {TradeStreamsRequest} requestParameters Request parameters.
     * @returns {WebsocketStream<TradeStreamsResponse>}
     * @throws {RequiredError}
     * @memberof PublicApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-derivatives-trading-options/api/ws-streams/public#trade-streams Binance API Documentation}
     */
    public tradeStreams(
        requestParameters: TradeStreamsRequest
    ): WebsocketStream<TradeStreamsResponse> {
        const stream = this.localVarParamCreator.tradeStreams(
            requestParameters?.symbol,
            requestParameters?.id
        );

        return createStreamHandler<TradeStreamsResponse>(
            this.websocketBase,
            stream,
            requestParameters?.id,
            'public'
        );
    }
}

export enum DiffBookDepthStreamsUpdateSpeedEnum {
    UPDATE_SPEED_100ms = '100ms',
    UPDATE_SPEED_500ms = '500ms',
}

export enum PartialBookDepthStreamsLevelEnum {
    LEVEL_5 = '5',
    LEVEL_10 = '10',
    LEVEL_20 = '20',
}

export enum PartialBookDepthStreamsUpdateSpeedEnum {
    UPDATE_SPEED_100ms = '100ms',
    UPDATE_SPEED_500ms = '500ms',
}
